import { acceptHMRUpdate, defineStore } from 'pinia'
import { jwtDecode, type JwtPayload } from 'jwt-decode'
import { useStoreTrainee } from './store.trainee'

import { IAccount, IToken, TokenRight } from '@/api/myedvenn/account'

import { navGuardLoginUrl } from '@/middleware/public.global'

type JwtP = JwtPayload & { expDate?: Date }

type JwtAccess = JwtP & IToken
type JwtRefresh = JwtP & Pick<IToken, 'userId'>

export type AccountState = {
  account: null | IAccount;

  token: null | {
    access: string;
    accessDecoded: JwtAccess;
    refresh: string;
    refreshDecoded: JwtRefresh;
  }
  rights: {
    isTrainee?: boolean;
    isUser?: boolean;
    hasRightWrite?: boolean;
    isRoleAdmin?: boolean;
    isRoleDeveloper?: boolean;
    isRoleAdviser?: boolean;
    isRoleMentor?: boolean;
    isRoleManager?: boolean;
    isRoleTrainer?: boolean;
    isRoleCanAccessInvoicing?: boolean;
    isRoleMarketing?: boolean;
  };
}

export const useStoreAccount = defineStore('account', {

  state: () => ({
    account: null,
    token: null,
    rights: {}
  } as AccountState),

  getters: {
    isLogged: state => !!state.token,
  },

  actions: {

    async login(email: string, password: string) {
      this.account = await this.$api.account.login({ email, password });
      if (this.rights.isTrainee)
        await useStoreTrainee().refresh();
    },

    setToken(accessToken: string, refreshToken?: string) {
      if (accessToken) accessToken = accessToken.replace(/^Bearer /, '')
      if (refreshToken) refreshToken = refreshToken.replace(/^Bearer /, '')

      if (!accessToken && !refreshToken)
        this.token = null
      else {
        const accessDecoded = accessToken ? jwtDecode<JwtAccess>(accessToken) : null
        if (accessDecoded?.exp) {
          accessDecoded.exp = accessDecoded.exp * 1000
          accessDecoded.expDate = new Date(accessDecoded.exp)
        }

        let refreshDecoded = refreshToken ? jwtDecode<JwtRefresh>(refreshToken) : null
        if (!refreshDecoded)
          refreshDecoded = this.token?.refreshDecoded
        else if (refreshDecoded?.exp) {
          refreshDecoded.exp = refreshDecoded.exp * 1000
          refreshDecoded.expDate = new Date(refreshDecoded.exp)
        }

        this.$state.token = {
          access: accessToken,
          refresh: refreshToken || this.token?.refresh || null,
          accessDecoded,
          refreshDecoded,
        }
        this.$state.rights = mapRights(accessDecoded?.rights)
      }
    },

    async refresh() {
      if (this.rights.isTrainee) {
        try {
          this.account = await this.$api.account.getMe({})
          await useStoreTrainee().refresh();
        } catch (e) {
          await this.logout()
        }
      }
    },

    async logout(withTarget = false) {
      try {
        this.$api.account.logout({}, { skipToken: true, skipErrorCode: 401 })
      } finally {
        const redirect = navGuardLoginUrl(
          withTarget ?
            (this.$router.currentRoute.value?.fullPath || window.location.pathname)
            :
            undefined
        )
        if (redirect)
          this.$router.push(redirect)
        else
          this.$router.go(0)
      }
    }

  }
})

function mapRights(rights?: TokenRight[]): AccountState['rights'] {
  const _rights = {
    isTrainee: false,
    isUser: false,

    isRoleAdmin: false,
    isRoleDeveloper: false,
    isRoleAdviser: false,
    isRoleMentor: false,
    isRoleManager: false,
    isRoleTrainer: false,
    isRoleCanAccessInvoicing: false,
    isRoleMarketing: false,

    hasRightWrite: true,
  }

  if (!rights) return _rights

  for (const r of rights) {
    if (r === TokenRight.INTERNAL)
      _rights.isUser = true
    else if (r === TokenRight.TRAINEE)
      _rights.isTrainee = true

    else if (r === TokenRight.ADMIN)
      _rights.isRoleAdmin = true
    else if (r === TokenRight.DEVELOPER)
      _rights.isRoleDeveloper = true
    else if (r === TokenRight.ADVISER)
      _rights.isRoleAdviser = true
    else if (r === TokenRight.MENTOR)
      _rights.isRoleMentor = true
    else if (r === TokenRight.MANAGER)
      _rights.isRoleManager = true
    else if (r === TokenRight.TRAINER)
      _rights.isRoleTrainer = true
    else if (r === TokenRight.INVOICE)
      _rights.isRoleCanAccessInvoicing = true
    else if (r === TokenRight.MARKETING)
      _rights.isRoleMarketing = true

    else if (r === TokenRight.LOGIN_AS)
      _rights.hasRightWrite = false

    else
      console.warn(`right unknown: ${r}`)
  }

  return _rights
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStoreAccount, import.meta.hot))
}